import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, AlertTriangle } from "lucide-react";
import api, { clearSession, setSession, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ADMIN_ROLES = ["admin", "super_admin"];

export default function AdminLogin() {
    const nav = useNavigate();
    const location = useLocation();
    const redirectTo = new URLSearchParams(location.search).get("next") || "/admin";
    const [form, setForm] = useState({ email: "", password: "", totp_code: "" });
    const [busy, setBusy] = useState(false);
    const [needTfa, setNeedTfa] = useState(false);
    const [wrongPortalRole, setWrongPortalRole] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setWrongPortalRole(null);
        try {
            const { data } = await api.post("/auth/login", form);
            if (data.two_factor_required && !data.access_token) {
                setNeedTfa(true);
                toast.info("Enter your 6-digit TOTP code to continue.");
                return;
            }
            // Enforce role at the portal doorway. Doctors and any unexpected
            // role must be turned away — no session persisted.
            if (!ADMIN_ROLES.includes(data.user.role)) {
                clearSession();
                setWrongPortalRole(data.user.role);
                toast.error("This portal is for administrators only.");
                return;
            }
            setSession(data.access_token, data.user);
            toast.success(`Welcome ${data.user.full_name}`);
            nav(redirectTo);
        } catch (err) {
            toast.error(errMsg(err, "Login failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0B1B33] text-white flex flex-col" data-testid="admin-login-page">
            {/* Ambient background — subtle grid + glow, no cliché purple gradients */}
            <div className="fixed inset-0 pointer-events-none opacity-40"
                 style={{
                     backgroundImage:
                         "radial-gradient(1200px 500px at 15% 0%, rgba(58,128,247,0.18), transparent 60%)," +
                         "radial-gradient(900px 400px at 90% 100%, rgba(15,118,110,0.12), transparent 60%)," +
                         "linear-gradient(transparent 95%, rgba(255,255,255,0.04) 95%)," +
                         "linear-gradient(90deg, transparent 95%, rgba(255,255,255,0.04) 95%)",
                     backgroundSize: "auto, auto, 40px 40px, 40px 40px",
                 }} />

            <header className="relative z-10 px-8 py-6 flex items-center justify-between">
                <div className="inline-flex items-center gap-3">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-11 w-11 object-contain" />
                    <div className="leading-tight">
                        <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">DOCTOR DIGITAL ID</div>
                        <div className="text-lg font-semibold">Admin Portal</div>
                    </div>
                </div>
                <a href="/login" className="text-[13px] text-white/60 hover:text-white transition-colors" data-testid="admin-login-goto-doctor">
                    Doctor sign-in →
                </a>
            </header>

            <main className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(28rem,92vw)] items-center gap-16 px-8 pb-16 max-w-[1400px] mx-auto w-full">
                <section className="hidden lg:block max-w-lg">
                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/50 mb-6">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#3A80F7]" /> restricted access
                    </div>
                    <h1 className="text-5xl font-bold leading-[1.05] tracking-tight">
                        Run the platform<br />with confidence.
                    </h1>
                    <p className="mt-6 text-white/70 text-[15px] leading-relaxed max-w-md">
                        Approvals, payments, AI job monitoring, publishing, leads &amp; audit — the operations
                        console you use to keep every doctor&rsquo;s digital identity spotless.
                    </p>
                    <ul className="mt-8 space-y-3 text-[13px] text-white/60">
                        <li className="flex items-center gap-3"><span className="h-1 w-6 bg-[#3A80F7]" /> Real-time revenue &amp; AMC monitoring</li>
                        <li className="flex items-center gap-3"><span className="h-1 w-6 bg-[#3A80F7]" /> Doctor review &amp; publishing queue</li>
                        <li className="flex items-center gap-3"><span className="h-1 w-6 bg-[#3A80F7]" /> AI job retry &amp; escalation console</li>
                    </ul>
                </section>

                <section className="w-full max-w-md ml-auto">
                    <form
                        onSubmit={submit}
                        className="bg-white/[0.04] backdrop-blur-xl rounded-2xl p-8 ring-1 ring-white/10 shadow-2xl"
                        data-testid="admin-login-form"
                    >
                        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#3A80F7] mb-6">
                            <Lock size={13} /> secure sign-in
                        </div>
                        <h2 className="text-2xl font-semibold">Admin sign-in</h2>
                        <p className="mt-2 text-sm text-white/60">
                            Restricted to platform administrators.
                        </p>

                        {wrongPortalRole && (
                            <div className="mt-5 flex items-start gap-3 rounded-xl bg-[#FEE2E2]/10 ring-1 ring-[#DC2626]/30 p-3 text-[13px]" data-testid="admin-login-wrong-role">
                                <AlertTriangle size={16} className="text-[#F87171] shrink-0 mt-0.5" />
                                <div className="text-white/80">
                                    You signed in as <b>{wrongPortalRole}</b>. This portal is for administrators.{" "}
                                    <a href="/login" className="text-[#3A80F7] font-semibold hover:underline">
                                        Use the doctor sign-in →
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="email" className="text-white/70 text-[12px] tracking-wider uppercase">Work email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="username"
                                    placeholder="admin@yourplatform.com"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-1 bg-white/10 border-white/10 text-white placeholder-white/30 focus:bg-white/15"
                                    data-testid="admin-login-email"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="password" className="text-white/70 text-[12px] tracking-wider uppercase">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="mt-1 bg-white/10 border-white/10 text-white placeholder-white/30 focus:bg-white/15"
                                    data-testid="admin-login-password"
                                    required
                                />
                            </div>
                            {needTfa && (
                                <div>
                                    <Label htmlFor="totp" className="text-white/70 text-[12px] tracking-wider uppercase inline-flex items-center gap-2">
                                        <KeyRound size={12} /> 6-digit TOTP code
                                    </Label>
                                    <Input
                                        id="totp"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        pattern="\d{6}"
                                        placeholder="123456"
                                        value={form.totp_code}
                                        onChange={(e) => setForm({ ...form, totp_code: e.target.value })}
                                        className="mt-1 bg-white/10 border-white/10 text-white placeholder-white/30 focus:bg-white/15 tracking-[0.32em] text-center font-mono"
                                        data-testid="admin-login-totp"
                                        required
                                    />
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            className="mt-6 w-full h-11 rounded-xl bg-[#3A80F7] hover:bg-[#2f6ce0] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ring-1 ring-white/10 shadow-lg shadow-[#3A80F7]/20"
                            data-testid="admin-login-submit"
                        >
                            {busy ? "Verifying…" : (needTfa ? "Verify code" : "Sign in to admin")}
                        </button>

                        <div className="mt-5 flex items-center justify-between text-[12px] text-white/50">
                            <a href="/forgot-password" className="hover:text-white transition-colors" data-testid="admin-login-forgot">
                                Forgot password?
                            </a>
                            <a href="/super-admin/login" className="hover:text-white transition-colors" data-testid="admin-login-goto-super">
                                Super admin →
                            </a>
                        </div>
                    </form>

                    <div className="mt-4 text-center text-[11px] text-white/40">
                        All access is logged. Unauthorized attempts will be traced.
                    </div>
                </section>
            </main>
        </div>
    );
}
